import { useEffect, useMemo, useState } from 'react';

/** Which node's drag-handle menu is open, so its handle stays visible. */
function useDragMenuOpenTaskId() {
  const [dragMenuOpenTaskId, setDragMenuOpenTaskId] = useState(null);

  useEffect(() => {
    const handleMenuOpened = (e) => {
      setDragMenuOpenTaskId(e.detail.taskId);
    };
    const handleMenuClosed = (e) => {
      setDragMenuOpenTaskId((prev) => (prev === e.detail.taskId ? null : prev));
    };

    window.addEventListener('taskDragMenuOpened', handleMenuOpened);
    window.addEventListener('taskDragMenuClosed', handleMenuClosed);
    return () => {
      window.removeEventListener('taskDragMenuOpened', handleMenuOpened);
      window.removeEventListener('taskDragMenuClosed', handleMenuClosed);
    };
  }, []);

  return dragMenuOpenTaskId;
}

/**
 * Handles only render for the hovered node, the single selection,
 * the open drag menu, and the open connection menu.
 */
export function useVisibleHandleTasks({
  tasks,
  allTasks = [],
  hoveredNodeId,
  connectionMenuTaskId,
  selectedNodes,
}) {
  const dragMenuOpenTaskId = useDragMenuOpenTaskId();

  const taskLookup = useMemo(() => {
    const map = new Map();
    (allTasks.length ? allTasks : tasks).forEach((task) => map.set(task.id, task));
    tasks.forEach((task) => map.set(task.id, task));
    return map;
  }, [allTasks, tasks]);

  const pinnedIds = useMemo(() => {
    const ids = new Set();
    if (dragMenuOpenTaskId) ids.add(dragMenuOpenTaskId);
    if (connectionMenuTaskId) ids.add(connectionMenuTaskId);
    if (selectedNodes?.size === 1) ids.add([...selectedNodes][0]);
    return ids;
  }, [dragMenuOpenTaskId, connectionMenuTaskId, selectedNodes]);

  const visibleHandleTasks = useMemo(() => {
    const ids = new Set(pinnedIds);
    if (hoveredNodeId) ids.add(hoveredNodeId);
    return [...ids]
      .map((id) => taskLookup.get(id))
      .filter(Boolean);
  }, [hoveredNodeId, pinnedIds, taskLookup]);

  const regularTasks = visibleHandleTasks.filter(
    (task) => task.nodeType !== 'decision' && task.nodeType !== 'milestone' && task.nodeType !== 'note',
  );
  const milestoneTasks = visibleHandleTasks.filter((task) => task.nodeType === 'milestone');
  const decisionTasks = visibleHandleTasks.filter((task) => task.nodeType === 'decision');

  return { visibleHandleTasks, regularTasks, milestoneTasks, decisionTasks, pinnedIds };
}
